// src/v3/ui/scheduler/scheduler-page.tsx

import type { ColumnDef } from "@tanstack/react-table";
import {
  Bell,
  ChevronDown,
  ChevronUp,
  Clock,
  Info,
  Loader2,
  Play,
  RefreshCw,
} from "lucide-react";
import { useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  ErrorState,
  JobRunForm,
  Loading,
  Progress,
  Select,
  SmartTable,
  type SmartTableColumnMeta,
} from "../_common";
import { useEvents } from "../_common";
import { JOB_STATUS_OBJ } from "../_deps";
import { t, type TranslationKey } from "../locales";
import { CronScheduleForm } from "./cron-schedule-form";
import { NotificationForm } from "./notification-form";
import {
  useRunJob,
  useSchedulerCron,
  useSchedulerJobLogs,
  useSchedulerJobs,
  useSchedulerNotifications,
  useSchedulerSymbolsCron,
  type JobInfo,
  type JobLogRow,
  type JobStatus,
} from "./scheduler.hooks";

const statusVariant: Record<
  JobStatus,
  "default" | "secondary" | "destructive" | "outline"
> = {
  idle: "secondary",
  running: "outline",
  completed: "default",
  failed: "destructive",
};

const SCHEDULE_LABEL_KEYS: Record<string, TranslationKey> = {
  daily: "scheduler.schedule.daily",
  "symbols-refresh": "scheduler.schedule.symbols-refresh",
};

const JOB_DESCRIPTION_KEYS: Record<string, TranslationKey> = {
  "Import Candles for Date": "scheduler.job.description.importCandles",
  "Identify Pinbars": "scheduler.job.description.identifyPinbars",
  "Process Symbols": "scheduler.job.description.processSymbols",
  "Sync Symbols": "scheduler.job.description.syncSymbols",
};

/**
 * Map job name → SSE progress event type(s) emitted by the backend.
 * Each job may emit one or more event types; we listen to all of them.
 */
const JOB_PROGRESS_EVENTS: Record<string, string[]> = {
  "Process Symbols": ["scheduler.process-symbols.progress"],
  "Identify Pinbars": ["pinbar.identify.progress"],
  "Sync Symbols": ["datasource.import-symbols.progress"],
  // "Import Candles for Date" — short job, no progress events
};

/** Flatten all event types we need to subscribe to. */
const ALL_PROGRESS_EVENT_TYPES = Array.from(
  new Set(Object.values(JOB_PROGRESS_EVENTS).flat()),
);

const DEFAULT_PAGE_SIZE = 50;

export function SchedulerPage() {
  const {
    jobs,
    isLoading: jobsLoading,
    error: jobsError,
    refetch: refetchJobs,
  } = useSchedulerJobs();
  const {
    runJob,
    isPending: runJobPending,
    error: runJobError,
  } = useRunJob();
  const {
    cronExpression,
    isLoading: cronLoading,
    error: cronError,
    successMessage: cronSuccess,
    updateCron,
    clearMessages: clearCronMessages,
  } = useSchedulerCron();
  const {
    cronExpression: symbolsCronExpression,
    isLoading: symbolsCronLoading,
    error: symbolsCronError,
    successMessage: symbolsCronSuccess,
    updateCron: updateSymbolsCron,
    clearMessages: clearSymbolsCronMessages,
  } = useSchedulerSymbolsCron();
  const {
    config: notificationConfig,
    isLoading: notificationsLoading,
    isSaving: notificationsSaving,
    isTesting: notificationsTesting,
    error: notificationsError,
    successMessage: notificationsSuccess,
    saveConfig: saveNotifications,
    testConfig: testNotifications,
    clearMessages: clearNotificationMessages,
  } = useSchedulerNotifications();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [expandedJob, setExpandedJob] = useState<string | null>(null);
  const [runningJobName, setRunningJobName] = useState<string | null>(null);
  const [historyJobFilter, setHistoryJobFilter] = useState<string>("");

  // SSE progress events for scheduler jobs (all known event types)
  const { events: progressEvents } = useEvents(ALL_PROGRESS_EVENT_TYPES);

  const {
    logs,
    total,
    isLoading: historyLoading,
    error: historyError,
    refetch: refetchHistory,
  } = useSchedulerJobLogs({
    limit: pageSize,
    offset: (page - 1) * pageSize,
    ...(historyJobFilter ? { jobName: historyJobFilter } : {}),
  });

  // Group jobs by scheduleName
  const groupedJobs = useMemo(() => {
    const groups: Record<string, JobInfo[]> = {};
    for (const job of jobs) {
      const key = job.scheduleName ?? "daily";
      if (!groups[key]) groups[key] = [];
      groups[key].push(job);
    }
    return groups;
  }, [jobs]);

  // Get latest progress for a job by matching its event types
  const getProgress = (
    jobName: string,
  ): { percent: number; message: string } | null => {
    const eventTypes = JOB_PROGRESS_EVENTS[jobName];
    if (!eventTypes || eventTypes.length === 0) return null;
    const jobEvents = progressEvents.filter((e) => eventTypes.includes(e.type));
    if (jobEvents.length === 0) return null;
    const latest = jobEvents[jobEvents.length - 1];
    if (!latest) return null;
    return {
      percent: latest.progress,
      message: latest.message ?? "",
    };
  };

  const columns: ColumnDef<JobLogRow>[] = [
    { accessorKey: "jobName", header: t("scheduler.column.job") },
    {
      accessorKey: "startedAt",
      header: t("scheduler.column.started"),
      meta: { format: "datetime" } satisfies SmartTableColumnMeta,
    },
    {
      accessorKey: "finishedAt",
      header: t("scheduler.column.finished"),
      meta: { format: "datetime" } satisfies SmartTableColumnMeta,
    },
    { accessorKey: "status", header: t("scheduler.column.status") },
    { accessorKey: "result", header: t("scheduler.column.result") },
  ];

  const handleRunWithParams = async (
    jobName: string,
    params: Record<string, unknown>,
  ) => {
    setRunningJobName(jobName);
    try {
      await runJob(jobName, params);
      setExpandedJob(null);
    } catch {
      // error surfaced via runJobError in the jobs card
    } finally {
      setRunningJobName(null);
    }
  };

  const handleRunJob = async (jobName: string) => {
    setRunningJobName(jobName);
    try {
      await runJob(jobName);
    } catch {
      // error surfaced via runJobError in the jobs card
    } finally {
      setRunningJobName(null);
    }
  };

  const jobNamesForFilter = useMemo(() => {
    const names = new Set<string>();
    for (const job of jobs) names.add(job.name);
    return Array.from(names).sort();
  }, [jobs]);

  return (
    <div className="space-y-4">
      {/* Daily Pipeline schedule */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            {t(SCHEDULE_LABEL_KEYS["daily"]!)}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <CronScheduleForm
            cronExpression={cronExpression}
            onSave={(expr) => updateCron(expr)}
            isLoading={cronLoading}
            error={cronError}
            successMessage={cronSuccess}
            onClearMessages={clearCronMessages}
          />
        </CardContent>
      </Card>

      {/* Symbols Refresh schedule */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5" />
            {t(SCHEDULE_LABEL_KEYS["symbols-refresh"]!)}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <CronScheduleForm
            cronExpression={symbolsCronExpression}
            onSave={(expr) => updateSymbolsCron(expr)}
            isLoading={symbolsCronLoading}
            error={symbolsCronError}
            successMessage={symbolsCronSuccess}
            onClearMessages={clearSymbolsCronMessages}
          />
        </CardContent>
      </Card>

      {/* Failure notifications */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            {t("scheduler.notifications.title")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {notificationsLoading && <Loading />}
          {!notificationsLoading && notificationConfig !== null && (
            <NotificationForm
              config={notificationConfig}
              isSaving={notificationsSaving}
              isTesting={notificationsTesting}
              error={notificationsError}
              successMessage={notificationsSuccess}
              onSave={saveNotifications}
              onTest={testNotifications}
              onClearMessages={clearNotificationMessages}
            />
          )}
        </CardContent>
      </Card>

      {/* Jobs grouped by schedule */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              {t("scheduler.cronJobs")}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void refetchJobs()}
              disabled={jobsLoading}
            >
              <RefreshCw className="h-4 w-4" />
              {t("scheduler.refresh")}
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {jobsLoading && <Loading />}
          {(jobsError !== null || runJobError !== null) && (
            <ErrorState
              message={runJobError ?? jobsError ?? ""}
              onRetry={() => void refetchJobs()}
            />
          )}
          {jobs.length === 0 &&
            jobsError === null &&
            runJobError === null &&
            !jobsLoading && <EmptyState message={t("scheduler.noJobs")} />}

          {Object.entries(groupedJobs).map(([scheduleName, scheduleJobs]) => (
            <div key={scheduleName} className="space-y-2">
              <h3 className="text-sm font-semibold text-muted-foreground">
                {SCHEDULE_LABEL_KEYS[scheduleName] ?? scheduleName}
              </h3>
              {scheduleJobs.map((job) => {
                const progress = getProgress(job.name);
                const isRunning =
                  job.status === JOB_STATUS_OBJ.RUNNING ||
                  runningJobName === job.name;
                return (
                  <div key={job.name} className="rounded-md border p-3">
                    <div className="flex items-center justify-between">
                      <div className="space-y-1">
                        <div className="font-medium">{job.name}</div>
                        {JOB_DESCRIPTION_KEYS[job.name] && (
                          <div className="text-xs text-muted-foreground">
                            {t(JOB_DESCRIPTION_KEYS[job.name]!)}
                          </div>
                        )}
                        {job.dependencies.length > 0 && (
                          <div className="text-xs text-muted-foreground">
                            {t("scheduler.dependsOn")}:{" "}
                            {job.dependencies.join(", ")}
                          </div>
                        )}
                        {job.lastRun && job.lastRun.startedAt && (
                          <div className="text-xs text-muted-foreground">
                            {t("scheduler.lastRun")}:{" "}
                            {new Date(
                              job.lastRun.startedAt,
                            ).toLocaleDateString()}
                            {job.lastRun.result && ` — ${job.lastRun.result}`}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={statusVariant[job.status]}>
                          {job.status === JOB_STATUS_OBJ.RUNNING && (
                            <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                          )}
                          {job.status}
                        </Badge>
                        {job.hasParams ? (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() =>
                              setExpandedJob(
                                expandedJob === job.name ? null : job.name,
                              )
                            }
                            disabled={isRunning || jobsLoading || runJobPending}
                          >
                            {expandedJob === job.name ? (
                              <ChevronUp className="h-4 w-4" />
                            ) : (
                              <ChevronDown className="h-4 w-4" />
                            )}
                            {t("scheduler.runWithParams")}
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            onClick={() => void handleRunJob(job.name)}
                            disabled={isRunning || jobsLoading || runJobPending}
                          >
                            <Play className="h-4 w-4" />
                            {t("scheduler.run")}
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Progress bar for running jobs */}
                    {isRunning && progress && (
                      <div className="mt-3 space-y-1">
                        <Progress value={progress.percent} />
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <span>{progress.message}</span>
                          <span>({progress.percent}%)</span>
                        </div>
                        <div className="flex items-center gap-1 text-xs text-blue-600">
                          <Info className="h-3 w-3" />
                          {t("scheduler.progressCanClose")}
                        </div>
                      </div>
                    )}

                    {/* Job params form */}
                    {job.hasParams && expandedJob === job.name && (
                      <div className="mt-3">
                        <JobRunForm
                          fields={job.paramsFields}
                          onRun={(params) =>
                            handleRunWithParams(job.name, params)
                          }
                          isRunning={isRunning}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Execution History */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            {t("scheduler.executionHistory")}
            <div className="flex items-center gap-2">
              <Select
                value={historyJobFilter}
                onChange={(value) => {
                  setHistoryJobFilter(value);
                  setPage(1);
                }}
                options={[
                  { value: "", label: t("scheduler.allJobs") },
                  ...jobNamesForFilter.map((name) => ({
                    value: name,
                    label: name,
                  })),
                ]}
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => void refetchHistory()}
                disabled={historyLoading}
              >
                <RefreshCw className="h-4 w-4" />
                {t("scheduler.refresh")}
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {historyLoading && <Loading />}
          {historyError !== null && (
            <ErrorState
              message={historyError}
              onRetry={() => void refetchHistory()}
            />
          )}
          {!historyLoading && historyError === null && logs.length === 0 && (
            <EmptyState message={t("scheduler.noHistory")} />
          )}
          {!historyLoading && historyError === null && logs.length > 0 && (
            <SmartTable
              columns={columns}
              data={logs}
              total={total}
              page={page}
              pageSize={pageSize}
              isLoading={false}
              error={null}
              filters={[]}
              sort={[]}
              presets={[]}
              activePresetId="default"
              onPageChange={(p) => setPage(p)}
              onPageSizeChange={(ps) => {
                setPageSize(ps);
                setPage(1);
              }}
              onFiltersChange={() => {}}
              onSortChange={() => {}}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
